import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Settings } from '../../settings.class';
import { Status } from '../../status.interface';
import { WorkareaHistory } from '../../workarea-history.interface';
import { WorkareaImage } from '../../workarea-image';
import { Workarea } from '../../workarea.class';
import { ApiResponse, ApiService } from '../api/api.service';
import { LocalStorage } from './../../local-storage.class';

@Injectable({
    providedIn: 'root',
})
export class WorkareaService {
    private lastCustomerId = 0;
    private _workareas: Workarea[];
    private _statussus: object = {};
    private _pendingPolygonGeojson: any = null;
    private _pendingPolygonArea: number = 0;
    private _pendingPolygonCentroid: { lat: number; lng: number } | null = null;

    constructor(private apiService: ApiService) {}

    /**
     * Store polygon GeoJSON data and calculated area to be used when creating a new workarea
     */
    public setPendingPolygon(geojson: any, area?: number, centroid?: { lat: number; lng: number }): void {
        this._pendingPolygonGeojson = geojson;
        this._pendingPolygonArea = area || 0;
        this._pendingPolygonCentroid = centroid || null;
    }

    /**
     * Get and clear the pending polygon GeoJSON data and area
     * Returns object with geojson, area, and centroid, or null if no data is stored
     */
    public getPendingPolygon(): { geojson: any; area: number; centroid: { lat: number; lng: number } | null } | null {
        if (!this._pendingPolygonGeojson) {
            return null;
        }
        const data = {
            geojson: this._pendingPolygonGeojson,
            area: this._pendingPolygonArea,
            centroid: this._pendingPolygonCentroid,
        };
        this._pendingPolygonGeojson = null;
        this._pendingPolygonArea = 0;
        this._pendingPolygonCentroid = null;
        return data;
    }

    /**
     * Clear the pending polygon without retrieving it
     */
    public clearPendingPolygon(): void {
        this._pendingPolygonGeojson = null;
        this._pendingPolygonArea = 0;
        this._pendingPolygonCentroid = null;
    }

    getHistory(workareaId) {
        return this.apiService.getCall$<WorkareaHistory[]>(`workareas/history/${workareaId}`);
    }

    transfer(ids: number[], customerProjectId: number) {
        return this.apiService.postCall$('workareas/transfer', {
            ids,
            customer_project_id: customerProjectId,
        });
    }

    public getAll(reload = false, statuses?: number[], hideImages = false): Promise<Array<Workarea>> {
        return new Promise((resolve, reject) => {
            if (this.lastCustomerId != LocalStorage.getCustomer()['id'] || reload) {
                return this.apiService
                    .getCall<ApiResponse<Workarea[]>>('workareas', {
                        statuses,
                        mapSettings: JSON.stringify(Settings.mapSettings),
                        hideImages,
                    })
                    .then(response => {
                        const workareas = response.data;
                        this._workareas = workareas;
                        this.lastCustomerId = LocalStorage.getCustomer()['id'];
                        resolve(workareas);
                    })
                    .catch(err => {
                        reject(err);
                    });
            }
            resolve(this._workareas);
        });
    }

    // Not cached: export filters must not replace the shared map/list data
    public getAllForExport(options: { statuses?: number[]; customerProjectId: number }): Observable<Workarea[]> {
        return this.apiService.getCallData$<Workarea[]>('workareas', {
            statuses: options.statuses,
            mapSettings: JSON.stringify(Settings.mapSettings),
            hideImages: true,
            customer_project_id: options.customerProjectId,
        });
    }

    public get(params?: any): Observable<ApiResponse<Workarea[]>> {
        return this.apiService.getCall$<Workarea[]>(`workareas/all`, {
            current_customer_id: LocalStorage.getCustomer()['id'],
            ...params,
        });
    }

    public addWorkareaToCacheList(workarea) {
        if (typeof this._workareas !== 'undefined') {
            this._workareas.push(workarea);
        }
    }

    public resetLastCustomerId() {
        this.lastCustomerId = 0;
    }

    public getDetails(id: number): Promise<Workarea> {
        return this.apiService.getCall<ApiResponse<Workarea>>(`workareas/${id}`).then(response => response.data);
    }

    public saveWorkarea(workarea: Workarea): Promise<Object> {
        return this.apiService
            .postCall<ApiResponse<Object>>(`workareas`, { workarea: workarea })
            .then(response => response.data);
    }

    public reserve(workarea: Workarea): Promise<Workarea> {
        return this.apiService
            .postCall<ApiResponse<Workarea>>(`workareas/reserve`, { workarea: workarea })
            .then(response => response.data);
    }

    public removeReservation(workarea: Workarea): Promise<Workarea> {
        return this.apiService
            .postCall<ApiResponse<Workarea>>(`workareas/reserve/remove`, { workarea: workarea })
            .then(response => response.data);
    }

    public getAvailableStatuses(customerType: string): Promise<Array<Status>> {
        return new Promise((resolve, reject) => {
            if (typeof this._statussus[customerType] === 'undefined') {
                return this.apiService
                    .getCall<ApiResponse<Status[]>>(`workareas/available-statuses`, { customerType })
                    .then(response => {
                        const statuses = response.data;
                        this._statussus[customerType] = statuses;
                        resolve(statuses);
                    })
                    .catch(err => {
                        reject(err);
                    });
            }
            resolve(this._statussus[customerType]);
        });
    }

    public saveWorkareaPicture(base64, workareaId, statusId, url) {
        return this.apiService.postCall$<{
            imageUrl: string;
            workareaImage: WorkareaImage;
        }>(`workareas/picture`, { base64, workareaId, statusId, url });
    }

    public deleteWorkareaPicture(workareaImageId: number): Observable<ApiResponse<void>> {
        return this.apiService.deleteCall$<void>(`workareas/picture/${workareaImageId}`);
    }
}
